import type { AdyenCheckoutError } from '@adyen/adyen-web';
import { AdyenCheckout, Bancontact, Card, Dropin } from '@adyen/adyen-web';
import { RefObject, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import {
  APP_ENVS,
  currentAppEnvironment,
  NOTIFICATION_TYPES,
} from '@savgroup-front-common/constants';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { buildNotification } from '@savgroup-front-common/core/helpers';
import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { MessageType } from '@savgroup-front-common/types';

import { useConfirmPaymentIntent } from '../hooks/useConfirmPaymentIntent';
import useCreatePaymentIntent from '../hooks/useCreatePaymentIntent';
import { useGetPaymentAccount } from '../hooks/useGetPaymentAccount';

import { adaptLanguageToAdyenLocale } from './adapters/adaptLanguageToAdyenLocale';
import { adaptPaymentIntentToAdyenSession } from './adapters/adaptPaymentIntentToAdyenSession';
import { getIsAdyenCheckoutError } from './helpers/getIsAdyenCheckoutError';
import { getIsSessionExpiredError } from './helpers/getIsSessionExpiredError';
import messages from './messages';
import { ADYEN_ENVS } from './PaymentAdyen.types';

const ADYEN_ENVIRONMENT =
  currentAppEnvironment === APP_ENVS.PROD ? ADYEN_ENVS.live : ADYEN_ENVS.test;

const SECURED_FIELDS_FONT_FAMILY = 'Inter,Roboto,Arial,Helvetica,sans-serif';

interface UsePaymentAdyenArgs {
  invoiceId: string;
  claimGroupId: string;
  onSubmitSuccess: () => Promise<void>;
  isFormCompleted: boolean;
}

interface UsePaymentAdyenReturn {
  containerRef: RefObject<HTMLDivElement | null>;
  isLoading: boolean;
}

export const usePaymentAdyen = ({
  invoiceId,
  claimGroupId,
  onSubmitSuccess,
  isFormCompleted,
}: UsePaymentAdyenArgs): UsePaymentAdyenReturn => {
  const intl = useIntl();
  const theme = useTheme();
  const { pushNotification } = useToasts();
  const { paymentAccountDto } = useGetPaymentAccount({ claimGroupId });

  const {
    paymentIntentClientSecret,
    isLoading,
    refetch: refetchPaymentIntent,
  } = useCreatePaymentIntent({
    invoiceId,
    onInvoiceAlreadyPaid: onSubmitSuccess,
  });

  const { handleConfirmPaymentIntent } = useConfirmPaymentIntent({
    invoiceId,
    onSuccess: onSubmitSuccess,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const locale = adaptLanguageToAdyenLocale({ language: intl.locale });
  const clientKey = paymentAccountDto?.publicToken;
  const cardName = safeFormattedIntlString(messages.cardPaymentMethodName);

  const session = useMemo(
    () =>
      adaptPaymentIntentToAdyenSession({
        paymentIntent: paymentIntentClientSecret,
      }),
    [paymentIntentClientSecret],
  );

  const securedFieldsStyles = useMemo(
    () => ({
      base: {
        color: theme.colors.mainTextColor,
        fontSize: theme.fonts.size.small,
        fontFamily: SECURED_FIELDS_FONT_FAMILY,
        fontWeight: `${theme.fonts.weight.normal}`,
      },
      error: {
        color: theme.colors.danger,
      },
      placeholder: {
        color: theme.colors.paragraphTextColor,
      },
    }),
    [theme],
  );

  const callbacksRef = useRef({
    handleConfirmPaymentIntent,
    pushNotification,
    refetchPaymentIntent,
  });
  const regeneratedExpiredSessionIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    callbacksRef.current = {
      handleConfirmPaymentIntent,
      pushNotification,
      refetchPaymentIntent,
    };
  });

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !session || !clientKey || !isFormCompleted) {
      return undefined;
    }

    let isIgnored = false;
    let dropinInstance: Dropin | undefined;

    const notifyPaymentFailure = ({
      technicalMessage,
      message = messages.paymentFailure,
    }: {
      technicalMessage?: string;
      message?: MessageType;
    } = {}) => {
      if (technicalMessage) {
        logError(technicalMessage);
      }

      callbacksRef.current.pushNotification(
        buildNotification({
          notificationType: NOTIFICATION_TYPES.ERROR,
          message,
        }),
      );
    };

    const setupDropin = async () => {
      const checkout = await AdyenCheckout({
        environment: ADYEN_ENVIRONMENT,
        clientKey,
        locale,
        session,
        onPaymentCompleted: (paymentCompletedData) => {
          callbacksRef.current
            .handleConfirmPaymentIntent({
              metadata:
                'sessionResult' in paymentCompletedData
                  ? {
                      sessionResult: paymentCompletedData.sessionResult,
                    }
                  : undefined,
            })
            .catch(() => undefined);
        },
        onPaymentFailed: () => {
          notifyPaymentFailure();
          dropinInstance?.setStatus('ready');
        },
        onError: (error: AdyenCheckoutError) => {
          const isSessionExpired = getIsSessionExpiredError(error);

          notifyPaymentFailure({
            technicalMessage: error.message,
            message: isSessionExpired
              ? messages.paymentSessionExpired
              : messages.paymentFailure,
          });

          if (
            !isSessionExpired ||
            regeneratedExpiredSessionIdRef.current === session.id
          ) {
            return;
          }

          regeneratedExpiredSessionIdRef.current = session.id;
          callbacksRef.current.refetchPaymentIntent();
        },
      });

      if (isIgnored) {
        return;
      }

      dropinInstance = new Dropin(checkout, {
        paymentMethodComponents: [Card, Bancontact],
        paymentMethodsConfiguration: {
          card: {
            name: cardName,
            hasHolderName: true,
            holderNameRequired: true,
            styles: securedFieldsStyles,
          },
        },
      }).mount(container) as Dropin;
    };

    setupDropin().catch((error: Error) => {
      if (getIsAdyenCheckoutError(error)) {
        return;
      }

      notifyPaymentFailure({ technicalMessage: error.message });
    });

    return () => {
      isIgnored = true;
      dropinInstance?.unmount();
    };
  }, [
    session,
    clientKey,
    locale,
    isFormCompleted,
    cardName,
    securedFieldsStyles,
  ]);

  return {
    containerRef,
    isLoading,
  };
};
